import { getApiUrl } from "@lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Artist {
  id: number;
  name: string;
  slug: string;
  created_at?: string;
  updated_at?: string;
}

export interface ArtistCreate {
  name: string;
  slug?: string;
}

export interface ArtistUpdate {
  name?: string;
  slug?: string;
}

export interface ArtistPaginatedResponse {
  list: Artist[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ArtistsQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

function getAuthToken(): string | null {
  return localStorage.getItem("admin_token");
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}${url}?token=${token}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }));
    throw new Error(error.detail || error.message || "Request failed");
  }

  return response.json();
}

async function getArtists(params?: ArtistsQueryParams): Promise<ArtistPaginatedResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  
  const queryParams = new URLSearchParams();
  queryParams.append("token", token);
  if (params?.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size) {
    queryParams.append("page_size", params.page_size.toString());
  }
  if (params?.search) {
    queryParams.append("search", params.search);
  }
  if (params?.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }
  
  const response = await fetch(`${apiUrl}/artist?${queryParams.toString()}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to fetch artists" }));
    throw new Error(error.detail || error.message || "Failed to fetch artists");
  }
  
  return response.json();
}

async function getArtist(id: number): Promise<Artist> {
  return fetchWithAuth(`/artist/${id}`);
}

async function createArtist(artist: ArtistCreate): Promise<Artist> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/artist?token=${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(artist),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create artist" }));
    throw new Error(error.detail || error.message || "Failed to create artist");
  }

  return response.json();
}

async function updateArtist(id: number, artist: ArtistUpdate): Promise<Artist> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/artist/${id}?token=${token}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(artist),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update artist" }));
    throw new Error(error.detail || error.message || "Failed to update artist");
  }

  return response.json();
}

async function deleteArtist(id: number): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/artist/${id}?token=${token}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to delete artist" }));
    throw new Error(error.detail || error.message || "Failed to delete artist");
  }
}

export function useArtists(params?: ArtistsQueryParams) {
  return useQuery({
    queryKey: ["artists", params],
    queryFn: () => getArtists(params),
  });
}

export function useArtist(id: number) {
  return useQuery({
    queryKey: ["artist", id],
    queryFn: () => getArtist(id),
    enabled: !!id,
  });
}

export function useCreateArtist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createArtist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
    },
  });
}

export function useUpdateArtist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ArtistUpdate }) => updateArtist(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
      queryClient.invalidateQueries({ queryKey: ["artist", variables.id] });
    },
  });
}

export function useDeleteArtist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArtist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artists"] });
    },
  });
}

